import { useDispatch } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { trackInternalEvent } from '@rio/tracking'
import { ButtonTheme, Button, ButtonV2 } from 'builder/components/Button'
import Icon24 from 'builder/components/Icon'
import Tooltip from 'builder/components/Tooltip'
import {
  actions,
  selectors as jobTrackingSelector,
  ColumnName,
  JobCreatePayload,
} from 'builder/modules/jobTracking'

import { stripHtml } from 'builder/utils/stripHTML'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import InterviewMock from 'builder/components/JobTracking/InterviewMock/InterviewMock'
import { usePortalNode } from 'builder/hooks/usePortalNode'
import { useAutoApply } from 'builder/views/AutoApply/hooks/useAutoApply'
import { useI18n } from 'builder/hooks/useI18n'
import CircleCheck from 'builder/components/FindJob/JobDetails/icon/CircleCheck.svg'
import {
  selectors as jobSearchSelectors,
  actions as jobSearchActions,
} from 'builder/modules/jobSearch'
import { useUser } from 'builder/hooks/useUser'
import { DocumentTypes } from 'builder/modules/constants'
import SelectResumeModal from 'builder/views/ResumeDistributionView/components/SelectResumeModal'
import { BriefResume, BriefExpertDoc } from 'builder/modules/panel'
import { Job } from 'builder/components/FindJob/JobSearchResult/types'
import { JobSearchModalEnum } from 'builder/modules/jobSearch/types'
import { goToBillingPlanPage } from 'builder/utils/goToBillingPlanPage'
import { useConfig } from 'builder/hooks/useConfig'
import { useQueryParam } from 'builder/hooks/useQueryParam'
import TickImage from '../Shape.svg'
import { TabType } from '../../types'
import { ApplyButtonIcon } from '../styles'
import { AutoTailoringPaywallModal } from '../../AutoTailoringPaywallModal'
import {
  AppliedButton,
  CheckIcon,
  AutoAppliedButton,
  AutoAppliedIcon,
  ActionButtonsContainer,
} from './styles'

type Props = {
  job: Job & { position?: number }
  setApplyDialog: (showDialog: boolean) => void
  mobileVertical?: boolean
  selectedTabId?: TabType
}

const ActionButtons = React.forwardRef((props: Props, ref: React.ForwardedRef<HTMLDivElement>) => {
  const { job, setApplyDialog, mobileVertical, selectedTabId } = props
  const dispatch = useDispatch()
  const { i18n } = useI18n()
  const {
    isAutoApplyLimitReached,
    redirectToBillingPlans,
    isAutoApplyFeatureEnabled,
    redirectToAutoApplyQuestionnaire,
  } = useAutoApply()
  const config = useConfig()
  const [triggerTailoringParam, setTriggerTailoringParam] = useQueryParam('trigger_tailor')
  const isAutotailoringEnabled = !!config?.features.autotailoringFromJobBoard

  const autoApplyBubbles = useTypedSelector(jobSearchSelectors.autoApplyBubbles)
  const modal = useTypedSelector(jobSearchSelectors.modal)

  const portalNode = usePortalNode()
  const [isInterviewPopupOpen, setInterviewPopupOpen] = useState<boolean>(false)
  const alreadyAppliedJob = useTypedSelector(jobTrackingSelector.selectAlreadyAppliedJob)
  const newTrackerCard: JobCreatePayload = {
    status: ColumnName.wishlist,
    title: job.job_title,
    company: job.company,
    link: job.url,
    description: stripHtml(job.description),
    open: true,
    external_slug_id: job.external_slug_id,
    location: job.location,
    logo: job.company_logo,
  }

  // check if current job is added
  const cards = useTypedSelector(jobTrackingSelector.selectJobs)
  const jobTrackingCard = cards.find(
    card =>
      card.externalSlugId === newTrackerCard.external_slug_id &&
      card.status !== ColumnName.recommended,
  )
  const isJobAutoApplied = cards.find(
    card =>
      card.company === newTrackerCard.company &&
      card.title === newTrackerCard.title &&
      card.externalSlugId === job.external_slug_id &&
      (card.status === ColumnName.auto_apply || card.autoApplyStatus !== null),
  )
  const user = useUser()
  const isFreePlan = !user?.hasPremiumFeatures
  const attempts = user?.publicAutoTailoringFreeAttempts
  const canTailorResume =
    !!user?.hasPremiumFeatures || (typeof attempts === 'number' && attempts > 0)

  const appliedCard = cards
    .filter(({ status }) => status === ColumnName.applied)
    .find(
      ({ company, title }) => company === newTrackerCard.company && title === newTrackerCard.title,
    )

  // auto close modal when component unmounts
  useEffect(() => {
    return () => {
      dispatch(jobSearchActions.setModal())
    }
  }, [dispatch])

  const openInNewTab = (url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const handleApply = useCallback(() => {
    openInNewTab(job.url)
    trackInternalEvent('apply_for_job', {
      title: job.job_title,
      position: job.position,
      parameter: selectedTabId,
      label: 'job_search',
    })
    setApplyDialog(true)
  }, [job, setApplyDialog])

  const handleAutoApply = useCallback(() => {
    if (redirectToBillingPlans()) {
      return
    }
    if (redirectToAutoApplyQuestionnaire()) {
      return
    }
    if (isAutoApplyLimitReached()) {
      return
    }
    trackInternalEvent('add_to_job_tracker', {
      title: job.job_title,
      label: 'save',
      position: job.position,
      parameter: selectedTabId,
    })
    trackInternalEvent('auto_apply_for_job', {
      title: job.job_title,
      label: 'job_search',
      position: job.position,
      parameter: selectedTabId,
    })
    if (jobTrackingCard) {
      const patchPayload = {
        id: jobTrackingCard.id,
        status: ColumnName.auto_apply,
        dummy: autoApplyBubbles.running,
      }
      dispatch(actions.patchJobRequest(patchPayload))
    } else {
      const createPayload = {
        ...newTrackerCard,
        status: ColumnName.auto_apply,
        open: false,
        location: job.location,
        dummy: autoApplyBubbles.running,
        isEasyApply: job.is_easy_apply,
      }
      dispatch(actions.createJobRequest(createPayload))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    job,
    jobTrackingCard,
    autoApplyBubbles,
    redirectToBillingPlans,
    redirectToAutoApplyQuestionnaire,
    isAutoApplyLimitReached,
  ])

  const triggerTailoring = useCallback(() => {
    if (!isAutotailoringEnabled) {
      return null
    }

    trackInternalEvent('click_tailor_resume', { label: isFreePlan ? 'free' : 'paid' })
    if (isFreePlan) {
      if (canTailorResume) {
        dispatch(jobSearchActions.setModal(JobSearchModalEnum.resume_select))
      } else {
        dispatch(jobSearchActions.setModal(JobSearchModalEnum.resume_select_paywall))
      }
    } else {
      dispatch(jobSearchActions.setModal(JobSearchModalEnum.resume_select))
    }
  }, [isAutotailoringEnabled, isFreePlan, canTailorResume, dispatch])

  useEffect(() => {
    if (triggerTailoringParam === 'true' && canTailorResume) {
      setTimeout(() => {
        triggerTailoring()
        setTriggerTailoringParam(null)
      }, 1000)
    }
  }, [triggerTailoringParam, canTailorResume, triggerTailoring, setTriggerTailoringParam])

  const applyButtons = useMemo(() => {
    if (isAutoApplyFeatureEnabled && !isFreePlan) {
      const manualApplyButton =
        alreadyAppliedJob || appliedCard ? (
          <AppliedButton>
            <CheckIcon src={TickImage} />
            {i18n.t('builder.job_search.auto_apply.action_buttons.manual_applied')}
          </AppliedButton>
        ) : (
          <Button onClick={handleApply} theme={ButtonTheme.secondary}>
            <ApplyButtonIcon />
            {i18n.t('builder.job_search.auto_apply.action_buttons.manual_apply')}
          </Button>
        )

      const getAutoApplyButtonComponent = (disabled: boolean) => (
        <Button isDisabled={disabled} onClick={handleAutoApply} id="auto_apply_button">
          <Icon24.Simple style={{ marginRight: '4px', width: '18px' }} />
          {i18n.t('builder.job_search.auto_apply.action_buttons.auto_apply')}
        </Button>
      )

      const autoApplyButtonForNotAppliedJobs = job.is_easy_apply ? (
        <Tooltip
          textAlign="center"
          position="top"
          style={{
            display: 'inline-flex',
          }}
          multiline={true}
          value={i18n.t('builder.job_search.auto_apply_disabled')}
        >
          {getAutoApplyButtonComponent(true)}
        </Tooltip>
      ) : (
        <Tooltip
          position="bottom"
          style={{
            display: 'inline-flex',
          }}
          multiline={true}
          value={i18n.t('builder.job_search.auto_apply_description')}
        >
          {getAutoApplyButtonComponent(false)}
        </Tooltip>
      )

      const autoApplyButton = isJobAutoApplied ? (
        <AutoAppliedButton>
          <AutoAppliedIcon src={CircleCheck} />
          {i18n.t('builder.job_search.auto_apply.action_buttons.applied')}
        </AutoAppliedButton>
      ) : (
        autoApplyButtonForNotAppliedJobs
      )

      return (
        <>
          {autoApplyButton}
          {manualApplyButton}
        </>
      )
    }

    if (alreadyAppliedJob || appliedCard) {
      return (
        <AppliedButton>
          <CheckIcon src={TickImage} />
          {i18n.t('builder.job_search.auto_apply.action_buttons.applied')}
        </AppliedButton>
      )
    }

    return (
      <ButtonV2
        onClick={handleApply}
        leadingIcon={<ApplyButtonIcon />}
        theme={isFreePlan ? ButtonTheme.secondary : ButtonTheme.default}
      >
        {i18n.t('builder.job_search.auto_apply.action_buttons.apply')}
      </ButtonV2>
    )
  }, [
    isFreePlan,
    alreadyAppliedJob,
    appliedCard,
    handleApply,
    handleAutoApply,
    i18n,
    isAutoApplyFeatureEnabled,
    isJobAutoApplied,
    job.is_easy_apply,
  ])

  const tailorResumeForFreeUser = useMemo(() => {
    if (!isFreePlan || !isAutotailoringEnabled) {
      return null
    }
    return (
      <ButtonV2
        leadingIcon={<Icon24.ArrowTargetAutoTailoring />}
        data-testid="tailor-resume-button"
        onClick={triggerTailoring}
      >
        {i18n.t('builder.job_search.job_details.tailor_resume.free')}
      </ButtonV2>
    )
  }, [i18n, isFreePlan, triggerTailoring, isAutotailoringEnabled])

  const tailorResumeForPaidUser = useMemo(() => {
    if (isFreePlan || !isAutotailoringEnabled) {
      return null
    }
    return (
      <ButtonV2
        leadingIcon={<Icon24.ArrowTargetAutoTailoring />}
        theme={ButtonTheme.secondary}
        data-testid="tailor-resume-button"
        onClick={triggerTailoring}
      >
        {i18n.t('builder.job_search.job_details.tailor_resume.paid')}
      </ButtonV2>
    )
  }, [i18n, isFreePlan, triggerTailoring, isAutotailoringEnabled])

  const handleOnCreateResume = () => {
    if (!canTailorResume) {
      dispatch(
        jobSearchActions.manualTailoringFromJS({
          cardId: jobTrackingCard?.id,
          resumeId: undefined,
          job,
        }),
      )
      return
    }

    dispatch(
      jobSearchActions.autoTailoringFromJS({
        cardId: jobTrackingCard?.id,
        resumeId: undefined,
        job,
      }),
    )
  }

  const handleSubmitSelectedResume = (resume: BriefResume | BriefExpertDoc) => {
    if (!canTailorResume) {
      dispatch(
        jobSearchActions.manualTailoringFromJS({
          cardId: jobTrackingCard?.id,
          resumeId: resume.id,
          job,
        }),
      )
      return
    }

    dispatch(
      jobSearchActions.autoTailoringFromJS({
        cardId: jobTrackingCard?.id,
        resumeId: resume.id,
        job,
      }),
    )
  }

  const navigateToBillingPlans = () => {
    goToBillingPlanPage({
      tailoring: true,
    })
  }

  return (
    <ActionButtonsContainer $mobileVertical={mobileVertical} ref={ref}>
      {tailorResumeForFreeUser}
      {applyButtons}
      {tailorResumeForPaidUser}

      {ReactDOM.createPortal(
        <InterviewMock
          visible={isInterviewPopupOpen}
          selectedJob={newTrackerCard}
          onClose={() => setInterviewPopupOpen(false)}
          trackingLabel="job_search"
        />,
        portalNode,
      )}

      {modal === JobSearchModalEnum.resume_select &&
        ReactDOM.createPortal(
          <SelectResumeModal
            title={i18n.t('builder.job_search.job_details.resume_select_modal')}
            onClose={() => dispatch(jobSearchActions.setModal())}
            submitSelectedResume={handleSubmitSelectedResume}
            viewOnly={DocumentTypes.resume}
            onCreateResume={handleOnCreateResume}
            disableDefaultCreateResumeBehavior
          />,
          document.body,
        )}
      {modal === JobSearchModalEnum.resume_select_paywall &&
        ReactDOM.createPortal(
          <AutoTailoringPaywallModal
            handleClose={() => dispatch(jobSearchActions.setModal())}
            handleClickEditManually={() =>
              dispatch(jobSearchActions.setModal(JobSearchModalEnum.resume_select))
            }
            handleUnlock={navigateToBillingPlans}
          />,
          document.body,
        )}
    </ActionButtonsContainer>
  )
})

export default ActionButtons
